import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useCountUp, useWidth } from './utils'

export function AnimatedNumber({ value, suffix = '', className, format }: {
  value: number; suffix?: string; className?: string; format?: (n: number) => string
}) {
  // Whole counts tick in whole steps; amounts like money keep two decimals so $114.90 lands on .90.
  const shown = useCountUp(value, 1200, Number.isInteger(value) ? 0 : 2)
  return <span className={cn('tabular-nums', className)}>{format ? format(shown) : shown.toLocaleString()}{suffix}</span>
}

export function Sparkline({ values, color, label }: { values: number[]; color: string; label: string }) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const height = 40
  const max = Math.max(1, ...values)
  const step = values.length > 1 ? width / (values.length - 1) : 0
  const points = values.map((v, i) => [i * step, height - 3 - (v / max) * (height - 6)] as const)
  const line = points.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join('')
  const area = points.length ? `${line}L${width},${height}L0,${height}Z` : ''
  const last = points.at(-1)
  const id = `spark-${label.replace(/\W/g, '')}`
  return <div ref={ref} className="h-10 w-full" role="img" aria-label={`${label} trend, latest ${values.at(-1) ?? 0}, peak ${max}`}>
    {width > 0 && values.length > 1 && <svg width={width} height={height} className="overflow-visible">
      <defs><linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity={0.28} /><stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient></defs>
      <path d={area} fill={`url(#${id})`} className="viz-fade" />
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" pathLength={1} className="viz-draw" />
      {last && <circle cx={last[0]} cy={last[1]} r={3.5} fill={color} stroke="hsl(var(--card))" strokeWidth={2} className="viz-pop" style={{ animationDelay: '1.2s' }} />}
    </svg>}
  </div>
}

export function KpiTile({ label, value, detail, delay = 0, children, suffix, format, icon }: {
  label: string; value: number; detail: ReactNode; delay?: number; children?: ReactNode; suffix?: string
  format?: (n: number) => string; icon?: ReactNode
}) {
  return <div className="viz-rise group relative overflow-hidden rounded-xl border bg-card p-5 shadow-subtle transition-shadow hover:shadow-card"
    style={{ animationDelay: `${delay}ms` } as CSSProperties}>
    <div className="flex items-start justify-between gap-3"><p className="text-sm text-muted-foreground">{label}</p>{icon && <span className="text-muted-foreground/70">{icon}</span>}</div>
    <p className="mt-1 font-serif text-4xl font-semibold leading-tight"><AnimatedNumber value={value} suffix={suffix} format={format} /></p>
    <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
    {children && <div className="mt-3">{children}</div>}
  </div>
}

/** Animated ring for a share of a whole, e.g. active artists out of enabled accounts. */
export function Ring({ value, total, color, size = 168, stroke = 14, children }: {
  value: number; total: number; color: string; size?: number; stroke?: number; children?: ReactNode
}) {
  const share = total ? Math.min(1, value / total) : 0
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const [drawn, setDrawn] = useState(0)
  useEffect(() => { const frame = requestAnimationFrame(() => setDrawn(share)); return () => cancelAnimationFrame(frame) }, [share])
  return <div className="relative shrink-0" style={{ width: size, height: size }}>
    <svg width={size} height={size} className="-rotate-90" aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--viz-empty)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={circumference * (1 - drawn)}
        className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-[1600ms] motion-safe:ease-out" />
    </svg>
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
  </div>
}
